import { useState, useEffect, useRef } from "react";
import ITAModal from "./ITAModal";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

function getToken() {
  const role = sessionStorage.getItem("activeRole");
  if (role) { const t = localStorage.getItem("token_" + role); if (t) return t; }
  for (const r of ["itadmin","superadmin","hr","employee","pm","intern"]) {
    const t = localStorage.getItem("token_" + r); if (t) return t;
  }
  return null;
}

async function apiCall(path, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers||{}), "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const text = await res.text();
  let data; try { data = JSON.parse(text); } catch { data = {}; }
  if (!res.ok) throw new Error(data?.error || data?.message || "Request failed");
  return data;
}

const fmtDT = (d, t) => {
  if (!d) return "—";
  try {
    const str = d.split("T")[0] + (t ? ` ${t}` : "");
    return new Date(str).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  } catch { return d; }
};

const audienceOptions = [
  { value: "all",  label: "All Employees", icon: "👥" },
  { value: "team", label: "Team Members (Employees & Interns)", icon: "🏢" },
  { value: "pm",   label: "Project Managers", icon: "📊" },
  { value: "project", label: "Project Members (choose project)", icon: "📁" },
];

// ── Evaluation Widget ──
function EvalWidget({ ann, onClose, onSent }) {
  const [comment, setComment] = useState("");
  const [selectedPmId, setSelectedPmId] = useState("");
  const [pmList, setPmList] = useState([]);
  const [sending, setSending] = useState(false);
  const [evals, setEvals] = useState([]);

  useEffect(() => {
    apiCall(`/itadmin/announcements/${ann.id || ann.ann_id}/evaluations`)
      .then(d => setEvals(Array.isArray(d) ? d : []))
      .catch(() => {});
    // Load PM list
    apiCall("/itadmin/pms-list")
      .then(d => { setPmList(Array.isArray(d) ? d : []); })
      .catch(() => {});
  }, [ann]);

  const send = async () => {
    if (!comment.trim()) { alert("Please enter a comment"); return; }
    if (!selectedPmId) { alert("Please select a PM to send to"); return; }
    setSending(true);
    try {
      const res = await apiCall("/itadmin/announcements/evaluate", { method: "POST", body: JSON.stringify({ annId: ann.id || ann.ann_id, comment, selectedPmId }) });
      onSent(res.pmName);
      onClose();
    } catch (e) { alert(e.message); }
    setSending(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:520, boxShadow:"0 24px 80px rgba(0,0,0,.25)" }}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#4f46e5,#7c3aed)", color:"#fff", padding:"14px 18px", borderRadius:"14px 14px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>📋 Evaluation</div>
            <div style={{ fontSize:12, opacity:.85, marginTop:2 }}>{ann.title}</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.2)", border:"none", color:"#fff", borderRadius:8, width:30, height:30, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        <div style={{ padding:18 }}>
          {/* Previous evals */}
          {evals.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#64748b", textTransform:"uppercase", marginBottom:8 }}>Previous Evaluations</div>
              {evals.map((e,i) => (
                <div key={i} style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", marginBottom:8, borderLeft:"3px solid #6366f1" }}>
                  <div style={{ fontSize:12, color:"#4f46e5", fontWeight:600, marginBottom:4 }}>{e.evaluator_name} → PM: {e.pm_name || "—"}</div>
                  <div style={{ fontSize:13, color:"#374151", marginBottom:6 }}>{e.comment}</div>
                  {e.pm_reply && (
                    <div style={{ background:"#dcfce7", borderRadius:6, padding:"6px 8px", fontSize:12, color:"#15803d" }}>
                      <strong>PM Reply:</strong> {e.pm_reply}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* PM Selector */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>Select PM to Send To *</label>
            <select
              value={selectedPmId}
              onChange={e => setSelectedPmId(e.target.value)}
              style={{ width:"100%", padding:"8px 10px", border:`1px solid ${selectedPmId ? "#6366f1" : "#d1d5db"}`, borderRadius:8, fontSize:13, background:"#fff", outline:"none", boxSizing:"border-box" }}>
              <option value="">— Choose a Project Manager —</option>
              {pmList.map(pm => (
                <option key={pm.id} value={pm.id}>{pm.name}</option>
              ))}
            </select>
            {pmList.length === 0 && <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>Loading PMs...</div>}
          </div>

          {/* Comment box */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>Your Evaluation Comment *</label>
            <textarea
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Write your evaluation about this announcement — effectiveness, reach, feedback..."
              style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box", outline:"none" }}
            />
          </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button onClick={onClose} style={{ padding:"8px 16px", border:"1px solid #e2e8f0", borderRadius:8, background:"#fff", cursor:"pointer", fontSize:13 }}>Cancel</button>
            <button onClick={send} disabled={sending} style={{ padding:"8px 18px", background:"#4f46e5", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13 }}>
              {sending ? "Sending..." : "📤 Send to PM"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Log Card ──
function AnnCard({ ann, onEvaluate }) {
  const audienceColor = ann.audience_type === "all" ? "#2563eb" : ann.audience_type === "pm" ? "#7c3aed" : ann.audience_type === "project" ? "#059669" : "#d97706";
  const fmtedDate = (() => {
    try { return new Date(ann.created_at).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return "—"; }
  })();

  return (
    <div style={{ border:"1px solid #e2e8f0", borderRadius:10, background:"#fff", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
      <div style={{ padding:"12px 16px", borderBottom:"1px solid #f1f5f9" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div style={{ flex:1, paddingRight:12 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#1e293b", marginBottom:4 }}>{ann.title}</div>
            <div style={{ fontSize:12, color:"#64748b", lineHeight:1.5 }}>{(ann.body||"").substring(0,120)}{ann.body?.length > 120 ? "..." : ""}</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
            <span style={{ background: audienceColor+"15", color:audienceColor, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>
              {ann.audience_label || ann.audience || "All"}
            </span>
            {ann.project_name && <span style={{ fontSize:11, color:"#059669", fontWeight:500 }}>📁 {ann.project_name}</span>}
          </div>
        </div>
      </div>
      <div style={{ padding:"8px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#fafafa" }}>
        <div style={{ fontSize:11, color:"#94a3b8" }}>📅 {fmtedDate} {ann.time ? `· ⏰ ${ann.time}` : ""}</div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {ann.eval_count > 0 && (
            <span style={{ fontSize:11, color:"#7c3aed", fontWeight:600 }}>💬 {ann.eval_count} eval</span>
          )}
          <button
            onClick={() => onEvaluate(ann)}
            style={{ padding:"4px 12px", background:"linear-gradient(135deg,#4f46e5,#7c3aed)", color:"#fff", border:"none", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>
            📋 Evaluation
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──
function ITAAnnouncePg() {
  const [anns, setAnns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [evalAnn, setEvalAnn] = useState(null);
  const [toast, setToast] = useState("");
  const [form, setForm] = useState({ title:"", body:"", audienceType:"all", projectId:"", date:"", time:"" });
  const [publishing, setPublishing] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const [annData, projData] = await Promise.all([
        apiCall("/itadmin/announcements").catch(() => []),
        apiCall("/itadmin/projects-list").catch(() => []),
      ]);
      setAnns(Array.isArray(annData) ? annData : []);
      setProjects(Array.isArray(projData) ? projData : []);
    } catch(e) { console.warn(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!form.title.trim() || !form.body.trim()) { alert("Title and body are required"); return; }
    if (form.audienceType === "project" && !form.projectId) { alert("Please select a project"); return; }
    setPublishing(true);
    try {
      await apiCall("/itadmin/announcements", { method:"POST", body: JSON.stringify({ title:form.title, body:form.body, audienceType:form.audienceType, projectId:form.projectId||null, date:form.date||null, time:form.time||null }) });
      showToast("Announcement published successfully!");
      setShowAdd(false);
      setForm({ title:"", body:"", audienceType:"all", projectId:"", date:"", time:"" });
      await load();
    } catch(e) { alert(e.message); }
    setPublishing(false);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#1e293b" }}> Announcements</h2>
          <p style={{ margin:"4px 0 0", color:"#64748b", fontSize:13 }}>Publish announcements to employees, teams, or project members</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{ padding:"8px 18px", background:"linear-gradient(135deg,#4f46e5,#7c3aed)", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
          ➕ New Announcement
        </button>
      </div>

      {toast && (
        <div style={{ background:"#dcfce7", border:"1px solid #bbf7d0", color:"#15803d", borderRadius:8, padding:"10px 14px", marginBottom:14, fontWeight:600, fontSize:13 }}>
          ✅ {toast}
        </div>
      )}

      {/* Log */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#94a3b8" }}>Loading announcements...</div>
      ) : anns.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, color:"#94a3b8" }}>
          <div style={{ fontWeight:600, marginBottom:4 }}>No announcements yet</div>
          <div style={{ fontSize:13 }}>Click "New Announcement" to publish one</div>
        </div>
      ) : (
        <div style={{ display:"grid", gap:10 }}>
          {anns.map(a => (
            <AnnCard key={a.ann_id || a.id} ann={{ ...a, id: a.ann_id || a.id }} onEvaluate={ann => setEvalAnn(ann)} />
          ))}
        </div>
      )}

      {/* Publish Modal */}
      <ITAModal open={showAdd} onClose={() => setShowAdd(false)} title="New Announcement">
        <div style={{ display:"grid", gap:14 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Title *</label>
            <input className="form-input" placeholder="Announcement title..." value={form.title} onChange={e => setForm({...form, title:e.target.value})} />
          </div>

          {/* Audience */}
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Audience *</label>
            <select className="form-input" value={form.audienceType} onChange={e => setForm({...form, audienceType:e.target.value, projectId:""})}>
              {audienceOptions.map(o => (
                <option key={o.value} value={o.value}>{o.icon} {o.label}</option>
              ))}
            </select>
          </div>

          {/* Project dropdown - only when project selected */}
          {form.audienceType === "project" && (
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Select Project *</label>
              <select className="form-input" value={form.projectId} onChange={e => setForm({...form, projectId:e.target.value})}>
                <option value="">— Choose a project —</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>{p.name} {p.pm_name ? `(PM: ${p.pm_name})` : ""}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Time</label>
              <input type="time" className="form-input" value={form.time} onChange={e => setForm({...form, time:e.target.value})} />
            </div>
          </div>

          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Message *</label>
            <textarea className="form-input" rows={5} placeholder="Write your announcement here..." value={form.body} onChange={e => setForm({...form, body:e.target.value})} style={{ resize:"vertical" }} />
          </div>

         
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button className="btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
            <button
              onClick={publish}
              disabled={publishing}
              style={{ padding:"8px 20px", background:"linear-gradient(135deg,#4f46e5,#7c3aed)", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
              {publishing ? "Publishing..." : " Publish"}
            </button>
          </div>
        </div>
      </ITAModal>

      {/* Evaluation Widget */}
      {evalAnn && (
        <EvalWidget
          ann={evalAnn}
          onClose={() => setEvalAnn(null)}
          onSent={(pmName) => showToast(`Evaluation sent to PM${pmName ? ": " + pmName : ""}!`)}
        />
      )}
    </div>
  );
}

export default ITAAnnouncePg;
